import type { MmPoint, MmRect, MmSize, ObjectTransform } from '../domain/types';

export type A4SheetOrientation = 'portrait' | 'landscape';

export const A4_SHEET_SIZE_MM = Object.freeze({ width: 210, height: 297 });
export const DEFAULT_A4_PAGE_PADDING_MM = 5;

export interface A4SheetLayout {
  format: 'A4';
  orientation: A4SheetOrientation;
  pageSizeMm: MmSize;
  /** Minimum clear edge around the complete transfer. */
  marginMm: number;
  printableSizeMm: MmSize;
  /** Position of the cropped artwork measured from the A4 sheet's top-left corner. */
  artworkOffsetMm: MmPoint;
  artworkSizeMm: MmSize;
}

export interface A4SheetClearanceMm {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface A4MaximumFit {
  orientation: A4SheetOrientation;
  printableSizeMm: MmSize;
  /** Uniform scale that fills as much of the selected A4 orientation as possible. */
  scale: number;
  fittedSizeMm: MmSize;
}

export type A4SheetFitResult =
  | {
      fits: true;
      layout: A4SheetLayout;
      clearanceMm: A4SheetClearanceMm;
    }
  | {
      fits: false;
      error: A4SheetFitError;
    };

export class A4SheetFitError extends RangeError {
  readonly artworkSizeMm: MmSize;
  readonly marginMm: number;
  readonly portraitPrintableSizeMm: MmSize;
  readonly landscapePrintableSizeMm: MmSize;

  constructor(options: {
    artworkSizeMm: MmSize;
    marginMm: number;
    portraitPrintableSizeMm: MmSize;
    landscapePrintableSizeMm: MmSize;
  }) {
    super(
      `Motív ${formatMm(options.artworkSizeMm.width)} × ${formatMm(options.artworkSizeMm.height)} mm ` +
        `sa pri okraji ${formatMm(options.marginMm)} mm nezmestí na jeden hárok A4.`,
    );
    this.name = 'A4SheetFitError';
    this.artworkSizeMm = options.artworkSizeMm;
    this.marginMm = options.marginMm;
    this.portraitPrintableSizeMm = options.portraitPrintableSizeMm;
    this.landscapePrintableSizeMm = options.landscapePrintableSizeMm;
  }
}

const FIT_TOLERANCE_MM = 1e-7;

function formatMm(value: number): string {
  return String(Math.round(value * 10) / 10);
}

function assertPositiveSize(size: MmSize): void {
  if (
    !Number.isFinite(size.width) ||
    !Number.isFinite(size.height) ||
    size.width <= 0 ||
    size.height <= 0
  ) {
    throw new RangeError('A4 artwork dimensions must be finite positive millimetres');
  }
}

function pageSize(orientation: A4SheetOrientation): MmSize {
  return orientation === 'portrait'
    ? { ...A4_SHEET_SIZE_MM }
    : { width: A4_SHEET_SIZE_MM.height, height: A4_SHEET_SIZE_MM.width };
}

export function a4PrintableSize(orientation: A4SheetOrientation, marginMm: number): MmSize {
  if (!Number.isFinite(marginMm) || marginMm < 0) {
    throw new RangeError('A4 sheet margin must be a finite non-negative millimetre value');
  }
  const page = pageSize(orientation);
  const printable = {
    width: page.width - marginMm * 2,
    height: page.height - marginMm * 2,
  };
  if (printable.width <= 0 || printable.height <= 0) {
    throw new RangeError('A4 sheet margin leaves no printable area');
  }
  return printable;
}

/**
 * Largest proportional size available on one A4 sheet. Both orientations are
 * evaluated so callers never have to duplicate the physical A4 dimensions.
 */
export function a4MaximumFit(artworkSizeMm: MmSize, marginMm: number): A4MaximumFit {
  assertPositiveSize(artworkSizeMm);
  const candidates: A4MaximumFit[] = (['portrait', 'landscape'] as const).map(orientation => {
    const printableSizeMm = a4PrintableSize(orientation, marginMm);
    const scale = Math.min(
      printableSizeMm.width / artworkSizeMm.width,
      printableSizeMm.height / artworkSizeMm.height,
    );
    return {
      orientation,
      printableSizeMm,
      scale,
      fittedSizeMm: {
        width: artworkSizeMm.width * scale,
        height: artworkSizeMm.height * scale,
      },
    };
  });
  const portrait = candidates[0]!;
  const landscape = candidates[1]!;
  return landscape.scale > portrait.scale ? landscape : portrait;
}

function rotatedBoundsSize(sizeMm: MmSize, rotationDeg: number): MmSize {
  const radians = (rotationDeg * Math.PI) / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  return {
    width: sizeMm.width * cosine + sizeMm.height * sine,
    height: sizeMm.width * sine + sizeMm.height * cosine,
  };
}

function transformVisualCentre(transform: ObjectTransform): MmPoint {
  const radians = (transform.rotationDeg * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return {
    x:
      transform.positionMm.x +
      (transform.sizeMm.width / 2) * cosine -
      (transform.sizeMm.height / 2) * sine,
    y:
      transform.positionMm.y +
      (transform.sizeMm.width / 2) * sine +
      (transform.sizeMm.height / 2) * cosine,
  };
}

function positionForVisualCentre(centre: MmPoint, sizeMm: MmSize, rotationDeg: number): MmPoint {
  const radians = (rotationDeg * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return {
    x: centre.x - (sizeMm.width / 2) * cosine + (sizeMm.height / 2) * sine,
    y: centre.y - (sizeMm.width / 2) * sine - (sizeMm.height / 2) * cosine,
  };
}

/**
 * Uniformly clamps a rotated object to one usable A4 page while preserving its
 * visual centre. It never enlarges an already valid transform.
 */
export function clampTransformToA4(transform: ObjectTransform, marginMm: number): ObjectTransform {
  const fit = a4MaximumFit(rotatedBoundsSize(transform.sizeMm, transform.rotationDeg), marginMm);
  const scale = Math.min(1, fit.scale);
  if (scale >= 1 - FIT_TOLERANCE_MM) return structuredClone(transform);

  const centre = transformVisualCentre(transform);
  const sizeMm = {
    width: transform.sizeMm.width * scale,
    height: transform.sizeMm.height * scale,
  };

  return {
    ...structuredClone(transform),
    positionMm: positionForVisualCentre(centre, sizeMm, transform.rotationDeg),
    sizeMm,
  };
}

/**
 * Replaces an object's local size around its existing visual centre and then
 * applies the A4 cap. Useful for numeric width/height controls where anchoring
 * a very large intermediate value at the top-left would otherwise move it.
 */
export function resizeTransformToA4(
  transform: ObjectTransform,
  sizeMm: MmSize,
  marginMm: number,
): ObjectTransform {
  assertPositiveSize(sizeMm);
  const candidate = {
    ...structuredClone(transform),
    positionMm: positionForVisualCentre(
      transformVisualCentre(transform),
      sizeMm,
      transform.rotationDeg,
    ),
    sizeMm: { ...sizeMm },
  };
  return clampTransformToA4(candidate, marginMm);
}

function fits(artwork: MmSize, printable: MmSize): boolean {
  return (
    artwork.width <= printable.width + FIT_TOLERANCE_MM &&
    artwork.height <= printable.height + FIT_TOLERANCE_MM
  );
}

/**
 * Place one complete garment-side transfer on exactly one A4 page without scaling or rotation.
 * The page orientation follows the artwork when both orientations fit, and falls back to the
 * only orientation that fits. Artwork is centred inside the physical sheet.
 */
export function resolveA4SheetLayout(artworkSizeMm: MmSize, marginMm: number): A4SheetLayout {
  assertPositiveSize(artworkSizeMm);
  const portraitPrintableSizeMm = a4PrintableSize('portrait', marginMm);
  const landscapePrintableSizeMm = a4PrintableSize('landscape', marginMm);
  const portraitFits = fits(artworkSizeMm, portraitPrintableSizeMm);
  const landscapeFits = fits(artworkSizeMm, landscapePrintableSizeMm);
  if (!portraitFits && !landscapeFits) {
    throw new A4SheetFitError({
      artworkSizeMm: { ...artworkSizeMm },
      marginMm,
      portraitPrintableSizeMm,
      landscapePrintableSizeMm,
    });
  }

  const preferred: A4SheetOrientation =
    artworkSizeMm.width > artworkSizeMm.height ? 'landscape' : 'portrait';
  const orientation: A4SheetOrientation =
    preferred === 'portrait'
      ? portraitFits
        ? 'portrait'
        : 'landscape'
      : landscapeFits
        ? 'landscape'
        : 'portrait';
  const pageSizeMm = pageSize(orientation);
  const printableSizeMm =
    orientation === 'portrait' ? portraitPrintableSizeMm : landscapePrintableSizeMm;

  return {
    format: 'A4',
    orientation,
    pageSizeMm,
    marginMm,
    printableSizeMm,
    artworkOffsetMm: {
      x: (pageSizeMm.width - artworkSizeMm.width) / 2,
      y: (pageSizeMm.height - artworkSizeMm.height) / 2,
    },
    artworkSizeMm: { ...artworkSizeMm },
  };
}

export function a4SheetClearance(layout: A4SheetLayout): A4SheetClearanceMm {
  return {
    top: layout.artworkOffsetMm.y,
    right: layout.pageSizeMm.width - layout.artworkOffsetMm.x - layout.artworkSizeMm.width,
    bottom: layout.pageSizeMm.height - layout.artworkOffsetMm.y - layout.artworkSizeMm.height,
    left: layout.artworkOffsetMm.x,
  };
}

/** Non-throwing fit result intended for editor/review UI. */
export function inspectA4SheetFit(artworkSizeMm: MmSize, marginMm: number): A4SheetFitResult {
  try {
    const layout = resolveA4SheetLayout(artworkSizeMm, marginMm);
    return { fits: true, layout, clearanceMm: a4SheetClearance(layout) };
  } catch (error) {
    if (error instanceof A4SheetFitError) return { fits: false, error };
    throw error;
  }
}

/** Stable Slovak blocking copy shared by validation and the UI. Returns null when the motif fits. */
export function formatA4SheetBlockingMessage(
  artworkSizeMm: MmSize,
  marginMm: number,
): string | null {
  const result = inspectA4SheetFit(artworkSizeMm, marginMm);
  if (result.fits) return null;
  return (
    `Motív má ${formatMm(artworkSizeMm.width)} × ` +
    `${formatMm(artworkSizeMm.height)} mm a nezmestí sa v mierke 1 : 1 na jeden hárok A4. ` +
    `Zmenšite ho tak, aby sa zmestil na jeden výrobný hárok.`
  );
}

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function rounded(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * Human-readable inspection drawing. This is deliberately separate from the production PDF,
 * because labels and guides must never enter the print/cut job.
 */
export function createA4LayoutInspectionSvg(options: {
  layout: A4SheetLayout;
  label: string;
  previewFilename: string;
  placementBoundsMm: MmRect;
  placementAreaMm: MmSize;
  dpi: number;
  quantity: number;
}): string {
  const { layout } = options;
  const pageX = 16;
  const pageY = 16;
  const sidebarWidth = 190;
  const viewWidth = layout.pageSizeMm.width + sidebarWidth + 48;
  const viewHeight = Math.max(layout.pageSizeMm.height + 32, 280);
  const artworkX = pageX + layout.artworkOffsetMm.x;
  const artworkY = pageY + layout.artworkOffsetMm.y;
  const printableX = pageX + layout.marginMm;
  const printableY = pageY + layout.marginMm;
  const sidebarX = pageX + layout.pageSizeMm.width + 24;
  const orientationLabel = layout.orientation === 'portrait' ? 'na výšku' : 'na šírku';
  const rightMm = layout.pageSizeMm.width - layout.artworkOffsetMm.x - layout.artworkSizeMm.width;
  const bottomMm =
    layout.pageSizeMm.height - layout.artworkOffsetMm.y - layout.artworkSizeMm.height;

  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
    `width="1100" height="760" viewBox="0 0 ${rounded(viewWidth)} ${rounded(viewHeight)}" ` +
    `data-not-for-print="true">\n` +
    `  <title>A4 výrobný výkres — ${escapeXml(options.label)}</title>\n` +
    `  <rect width="100%" height="100%" fill="#f3f4f6"/>\n` +
    `  <rect x="${rounded(pageX)}" y="${rounded(pageY)}" width="${rounded(layout.pageSizeMm.width)}" height="${rounded(layout.pageSizeMm.height)}" fill="#fff" stroke="#111" stroke-width="0.8"/>\n` +
    `  <rect x="${rounded(printableX)}" y="${rounded(printableY)}" width="${rounded(layout.printableSizeMm.width)}" height="${rounded(layout.printableSizeMm.height)}" fill="none" stroke="#ef172f" stroke-width="0.45" stroke-dasharray="3 2"/>\n` +
    `  <image href="${escapeXml(options.previewFilename)}" xlink:href="${escapeXml(options.previewFilename)}" x="${rounded(artworkX)}" y="${rounded(artworkY)}" width="${rounded(layout.artworkSizeMm.width)}" height="${rounded(layout.artworkSizeMm.height)}" preserveAspectRatio="none"/>\n` +
    `  <rect x="${rounded(artworkX)}" y="${rounded(artworkY)}" width="${rounded(layout.artworkSizeMm.width)}" height="${rounded(layout.artworkSizeMm.height)}" fill="none" stroke="#2563eb" stroke-width="0.6"/>\n` +
    `  <line x1="${rounded(pageX + layout.pageSizeMm.width / 2)}" y1="${rounded(pageY)}" x2="${rounded(pageX + layout.pageSizeMm.width / 2)}" y2="${rounded(pageY + layout.pageSizeMm.height)}" stroke="#9ca3af" stroke-width="0.25" stroke-dasharray="2 2"/>\n` +
    `  <g font-family="Arial, sans-serif" fill="#111">\n` +
    `    <text x="${rounded(sidebarX)}" y="28" font-size="11" font-weight="700">${escapeXml(options.label)}</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="48" font-size="7">Hárok: A4 ${orientationLabel} (${rounded(layout.pageSizeMm.width)} × ${rounded(layout.pageSizeMm.height)} mm)</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="61" font-size="7">Motív: ${rounded(layout.artworkSizeMm.width)} × ${rounded(layout.artworkSizeMm.height)} mm, mierka 1 : 1</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="74" font-size="7">Tlačový raster: ${rounded(options.dpi)} DPI</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="87" font-size="7">Počet rovnakých hárkov: ${Math.max(1, Math.trunc(options.quantity))}</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="108" font-size="7" font-weight="700">Poloha motívu na A4</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="121" font-size="7">ľavý ${rounded(layout.artworkOffsetMm.x)} mm · horný ${rounded(layout.artworkOffsetMm.y)} mm</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="134" font-size="7">pravý ${rounded(rightMm)} mm · dolný ${rounded(bottomMm)} mm</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="155" font-size="7" font-weight="700">Poloha na odeve</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="168" font-size="7">X ${rounded(options.placementBoundsMm.x)} mm · Y ${rounded(options.placementBoundsMm.y)} mm</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="181" font-size="7">referenčná plocha ${rounded(options.placementAreaMm.width)} × ${rounded(options.placementAreaMm.height)} mm</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="211" font-size="6.4" fill="#b91c1c">KONTROLA — NIE JE TO TLAČOVÝ SÚBOR</text>\n` +
    `    <text x="${rounded(sidebarX)}" y="224" font-size="6.4">Na výrobu použite iba *-print-cut.pdf.</text>\n` +
    `  </g>\n` +
    `</svg>\n`
  );
}
